import React, { useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BooksSection from '../books/BooksSection';
import UsersSection from '../users/UsersSection';
import OrdersSection from '../orders/OrdersSection';
import ModeSection from '../ops/ModeSection';
import ServerHealthSection from '../ops/ServerHealthSection';
import IncidentsSection from '../ops/IncidentsSection';
import EmailsSection from '../ops/EmailsSection';
import ProvidersSection from '../catalog/ProvidersSection';
import ProductsSection from '../catalog/ProductsSection';
import PricingSection from '../catalog/PricingSection';
import DashboardSection from '../stats/DashboardSection';
import TrafficSection from '../stats/TrafficSection';
import AdminJobs from '../AdminJobs';
import AdminAnonymousPurge from '../AdminAnonymousPurge';
import AdminEvents from '../AdminEvents';
import AdminIcon from './AdminIcon';
import { adminPath } from './adminNav';

// Une page par section du menu (refonte de l'Admin, phase 1). Les ecrans existants sont DEPLACES tels quels dans leur section,
// sans changer leur contenu ni leur logique ; seuls le titre de page et la navigation sont nouveaux.
// (Leur texte reste en francais pour l'instant : chaque bloc sera traduit avec la phase qui le refond.)

function PageHead({ cle }) {
  const { t } = useTranslation('admin');
  return (
    <div className="adm-head">
      <h1>{t(`pages.${cle}.title`)}</h1>
      <p>{t(`pages.${cle}.subtitle`)}</p>
    </div>
  );
}

// Ecran herite, deplace tel quel : affiche sur une surface claire (voir .adm-legacy dans admin-shell.css).
const Legacy = ({ children }) => <div className="adm-legacy">{children}</div>;

export function DashboardPage() {
  const { t } = useTranslation('admin');
  const { alertes } = useOutletContext() || { alertes: { failedOrders: 0, incident: null, visits: null } };
  const aucune = !alertes.failedOrders && !alertes.incident && !alertes.visits;
  return (
    <>
      <PageHead cle="dashboard" />
      {alertes.failedOrders > 0 && (
        <div className="adm-alert is-bad" role="alert">
          <AdminIcon name="warn" />
          <div>
            <b>{t('pages.dashboard.failedOrders', { count: alertes.failedOrders })}</b>
            <p>{t('pages.dashboard.failedOrdersHelp')}</p>
          </div>
          <Link className="btn btn-primary btn-sm" to={`${adminPath('orders')}?statut=echec`}>{t('pages.dashboard.see')}</Link>
        </div>
      )}
      {alertes.incident && (
        <div className="adm-alert" role="status">
          <AdminIcon name="warn" />
          <div>
            <b>{t('pages.dashboard.incident', { date: new Date(alertes.incident.at).toLocaleString() })}</b>
            <p>{t('pages.dashboard.incidentHelp')}</p>
          </div>
          <Link className="btn btn-outline btn-sm" to={adminPath('operations')}>{t('pages.dashboard.see')}</Link>
        </div>
      )}
      {alertes.visits && alertes.visits.visitors > 0 && (
        <div className="adm-alert" role="status">
          <AdminIcon name="chart" />
          <div>
            <b>{t('pages.dashboard.newVisits', { count: alertes.visits.visitors })}</b>
            <p>{t('pages.dashboard.newVisitsHelp', { date: new Date(alertes.visits.lastAt).toLocaleString() })}</p>
          </div>
          <Link className="btn btn-outline btn-sm" to={`${adminPath('traffic')}?vue=detail`}>{t('pages.dashboard.see')}</Link>
        </div>
      )}
      {aucune && <p className="adm-card adm-note">{t('pages.dashboard.allClear')}</p>}
      <DashboardSection />
    </>
  );
}

export const OrdersPage = () => (<><PageHead cle="orders" /><OrdersSection /></>);

export const BooksPage = () => (<><PageHead cle="books" /><BooksSection /></>);

// Le nettoyage des comptes anonymes abandonnés vit ici (c'est une affaire de comptes) ; l'ancien écran est repris tel quel.
export const UsersPage = () => (<><PageHead cle="users" /><UsersSection /><Legacy><AdminAnonymousPurge /></Legacy></>);
export const ProvidersPage = () => (<><PageHead cle="providers" /><ProvidersSection /></>);
export const ProductsPage = () => (<><PageHead cle="products" /><ProductsSection /></>);
export const PricingPage = () => (<><PageHead cle="pricing" /><PricingSection /></>);
export function TrafficPage() {
  const { marquerTraficVu } = useOutletContext() || {};
  // Ouvrir le Trafic = avoir vu les nouvelles visites : la pastille du menu repart de zéro.
  useEffect(() => { if (marquerTraficVu) marquerTraficVu(); }, [marquerTraficVu]);
  return (<><PageHead cle="traffic" /><TrafficSection /></>);
}
export const SettingsPage = () => (<><PageHead cle="mode" /><ModeSection /></>);
export const EmailsPage = () => (<><PageHead cle="emails" /><EmailsSection /></>);

export function OperationsPage() {
  return (
    <>
      <PageHead cle="operations" />
      {/* Ce que fait la machine en ce moment, puis le journal des incidents (blocages, commandes en échec, sauvegardes). */}
      <div className="adm-page-gap">
        <ServerHealthSection />
        <IncidentsSection />
      </div>
      <Legacy>
        {/* Les travaux en mémoire et le journal des évènements : écrans repris tels quels. */}
        <AdminJobs />
        {/* Le journal des evenements : le deroule de ce qui est arrive aux livres et aux commandes, tous serveurs confondus. */}
        <AdminEvents />
      </Legacy>
    </>
  );
}
